// Crew Packets · Live project-based field packet.
//
// Product Owner decision (2026-07-20): Crew Packet = live project-
// based field packet; Work Order = snapshot document. Two distinct
// concepts. This page renders the LIVE view — no snapshot storage,
// no offline copy, no data duplication. Every field is projected
// from the current project + estimate + work-areas + schedule state
// at request time by `GET /api/projects/{pid}/crew-packet`.
//
// Print-friendly and mobile-first: single-column vertical stack with
// tight typography suitable for a phone in the field.
import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, API } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useLeviWorkspaceHint } from "@/lib/leviWorkspace";
import {
  ArrowLeft, MapPin, Users, CalendarClock, Camera, ClipboardList,
  Ruler, ShieldAlert, Package, HardHat, Printer, RefreshCw,
} from "lucide-react";

const CrewPacket = () => {
  const { t, lang } = useLang();
  const { pid } = useParams();
  // Phase 5A · Publish the current-page workspace hint. The backend
  // re-verifies the ID against the authenticated tenant before use.
  useLeviWorkspaceHint("crew_packet", {
    active_project_id: pid,
    current_section: "crew_packet",
  });
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true); setError(null);
    api.get(`/projects/${pid}/crew-packet`)
      .then((r) => setPacket(r.data))
      .catch((e) => setError(e?.response?.data?.detail?.error || "load_failed"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [pid]); // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleLabel = useMemo(() => {
    if (!packet?.schedule) return null;
    const s = packet.schedule;
    const locale = lang === "es" ? "es-ES" : "en-US";
    const fmt = (iso) => {
      if (!iso) return "";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return s.all_day
        ? d.toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" })
        : d.toLocaleString(locale, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    };
    return `${fmt(s.start_at)} → ${fmt(s.end_at)}`;
  }, [packet, lang]);

  return (
    <DashboardLayout
      title={packet?.customer?.name || t("crew_packet.title_fallback")}
      subtitle={packet?.job_name || t("crew_packet.subtitle_fallback")}
      actions={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="crew-packet-print-btn"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
          >
            <Printer size={13} /> {t("crew_packet.print")}
          </button>
          <button
            type="button"
            onClick={load}
            data-testid="crew-packet-refresh-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
          >
            <RefreshCw size={13} /> {t("crew_packet.refresh")}
          </button>
          <Link
            to={`/app/projects/${pid}`}
            data-testid="crew-packet-back-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
          >
            <ArrowLeft size={14} /> {t("crew_packet.back")}
          </Link>
        </div>
      }
    >
      {loading ? (
        <div className="text-sm text-[var(--jc-text-soft)]" data-testid="crew-packet-loading">
          {t("crew_packet.loading")}
        </div>
      ) : error ? (
        <div className="text-sm text-red-700" data-testid="crew-packet-error">
          {t("crew_packet.error")}
        </div>
      ) : packet ? (
        <div data-testid="crew-packet-page" className="max-w-3xl space-y-5">
          {/* Live-reference banner */}
          <div
            data-testid="crew-packet-live-banner"
            className="rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-2 text-xs flex items-start gap-2"
          >
            <ClipboardList size={14} className="mt-0.5 shrink-0" />
            <span>{t("crew_packet.live_banner")}</span>
          </div>

          {/* Job header */}
          <section className="jc-card p-5" data-testid="crew-packet-header">
            <div className="overline mb-2">{t("crew_packet.section_job")}</div>
            <div className="font-display text-xl mb-1">{packet.customer?.name || "—"}</div>
            <div className="text-sm text-[var(--jc-text-soft)] flex items-start gap-1.5">
              <MapPin size={13} className="mt-0.5 shrink-0" />
              <span data-testid="crew-packet-address">{packet.job_address || t("crew_packet.no_address")}</span>
            </div>
            <div className="text-sm mt-1"><span className="text-[var(--jc-text-soft)]">{t("crew_packet.job_name_prefix")}</span> {packet.job_name || "—"}</div>
            {packet.job_description && (
              <div className="text-sm mt-1 whitespace-pre-wrap" data-testid="crew-packet-description">
                {packet.job_description}
              </div>
            )}
            {packet.urgency && (
              <div className="text-xs mt-2 inline-flex px-2 py-0.5 rounded-full uppercase tracking-wider font-bold bg-[var(--jc-bg)] border border-[var(--jc-border)]">
                {t(`crew_packet.urgency.${packet.urgency}`)}
              </div>
            )}
          </section>

          {/* Schedule + Assignee */}
          <section className="jc-card p-5" data-testid="crew-packet-schedule">
            <div className="overline mb-2 flex items-center gap-1.5">
              <CalendarClock size={13} /> {t("crew_packet.section_schedule")}
            </div>
            {packet.schedule ? (
              <>
                <div className="font-display text-base">{packet.schedule.title || t("crew_packet.untitled_event")}</div>
                <div className="text-sm text-[var(--jc-text-soft)] mt-0.5" data-testid="crew-packet-schedule-when">
                  {scheduleLabel}
                </div>
                <div className="text-xs mt-1 inline-block px-1.5 py-0.5 rounded font-bold uppercase tracking-wider bg-[var(--jc-bg)] border border-[var(--jc-border)] text-[var(--jc-text-soft)]">
                  {t(`crew_packet.status.${packet.schedule.status || "scheduled"}`)}
                </div>
                {packet.schedule.internal_notes && (
                  <div className="text-sm mt-2 whitespace-pre-wrap">{packet.schedule.internal_notes}</div>
                )}
              </>
            ) : (
              <div className="text-sm text-[var(--jc-text-soft)]" data-testid="crew-packet-not-scheduled">
                {t("crew_packet.not_scheduled")}
              </div>
            )}
            <div className="mt-3 flex items-center gap-1.5 text-sm">
              <Users size={13} className="text-[var(--jc-text-soft)]" />
              <span data-testid="crew-packet-assignee">
                {packet.assignee
                  ? `${packet.assignee.display_name} · ${t(`crew_packet.assignee_kind.${packet.assignee.kind}`)}`
                  : t("crew_packet.unassigned")}
              </span>
            </div>
          </section>

          {/* Organized Scope */}
          <section className="jc-card p-5" data-testid="crew-packet-scope">
            <div className="overline mb-2 flex items-center gap-1.5">
              <ClipboardList size={13} /> {t("crew_packet.section_scope")}
            </div>
            {packet.organized_scope?.length > 0 ? (
              <ul className="divide-y divide-[var(--jc-border)]">
                {packet.organized_scope.map((li, idx) => (
                  <li key={idx} className="py-2 text-sm" data-testid={`crew-packet-scope-row-${idx}`}>
                    <div className="font-semibold">{li.name || t("crew_packet.untitled_line")}</div>
                    {li.description && (
                      <div className="text-[var(--jc-text-soft)] whitespace-pre-wrap">{li.description}</div>
                    )}
                    <div className="text-xs mt-0.5 text-[var(--jc-text-soft)]">
                      {li.quantity ?? "—"} {li.unit || ""}
                      {li.notes ? <> · {li.notes}</> : null}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-[var(--jc-text-soft)]" data-testid="crew-packet-scope-empty">
                {t("crew_packet.scope_empty")}
              </div>
            )}
          </section>

          {/* Work Areas */}
          <section className="jc-card p-5" data-testid="crew-packet-work-areas">
            <div className="overline mb-2 flex items-center gap-1.5">
              <Ruler size={13} /> {t("crew_packet.section_work_areas")}
            </div>
            {packet.work_areas?.length > 0 ? (
              <ul className="space-y-2">
                {packet.work_areas.map((wa, idx) => (
                  <li key={wa.id || idx} className="text-sm" data-testid={`crew-packet-work-area-${idx}`}>
                    <div className="font-semibold">{wa.name || t("crew_packet.untitled_area")}</div>
                    <div className="text-xs text-[var(--jc-text-soft)]">
                      {wa.area_type ? t(`crew_packet.area_type.${wa.area_type}`, wa.area_type) : ""}
                    </div>
                    {wa.calculated && (
                      <div className="text-xs mt-1 grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0.5">
                        {Object.entries(wa.calculated).map(([k, v]) => (
                          v == null ? null : (
                            <div key={k}>
                              <span className="text-[var(--jc-text-soft)]">{k}:</span> <span>{v}</span>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-[var(--jc-text-soft)]" data-testid="crew-packet-work-areas-empty">
                {t("crew_packet.work_areas_empty")}
              </div>
            )}
          </section>

          {/* Measurements */}
          <section className="jc-card p-5" data-testid="crew-packet-measurements">
            <div className="overline mb-2">{t("crew_packet.section_measurements")}</div>
            {packet.measurements_text ? (
              <p className="text-sm whitespace-pre-wrap">{packet.measurements_text}</p>
            ) : (
              <div className="text-sm text-[var(--jc-text-soft)]">{t("crew_packet.measurements_empty")}</div>
            )}
          </section>

          {/* Photos — reuse the same authenticated /files/{id} URL that
              PhotoUploader uses so we never duplicate storage or invent
              a second path. */}
          <section className="jc-card p-5" data-testid="crew-packet-photos">
            <div className="overline mb-2 flex items-center gap-1.5">
              <Camera size={13} /> {t("crew_packet.section_photos")}
            </div>
            {packet.photos?.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {packet.photos.map((p, idx) => {
                  const token = typeof window !== "undefined"
                    ? localStorage.getItem("jc_token") : null;
                  const src = `${API}/files/${p.id}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
                  return (
                    <a
                      key={p.id || idx}
                      href={src}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`crew-packet-photo-${idx}`}
                      className="block aspect-square overflow-hidden rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)]"
                    >
                      <img
                        src={src}
                        alt={p.original_filename || ""}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </a>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-[var(--jc-text-soft)]" data-testid="crew-packet-photos-empty">
                {t("crew_packet.photos_empty")}
              </div>
            )}
          </section>

          {/* Safety / Material / Internal notes */}
          <section className="jc-card p-5 grid gap-4 sm:grid-cols-2" data-testid="crew-packet-notes">
            <NoteBlock
              icon={ShieldAlert}
              title={t("crew_packet.section_safety")}
              body={packet.safety_notes}
              empty={t("crew_packet.safety_empty")}
              testid="crew-packet-safety"
            />
            <NoteBlock
              icon={Package}
              title={t("crew_packet.section_materials")}
              body={packet.material_notes}
              empty={t("crew_packet.materials_empty")}
              testid="crew-packet-materials"
            />
            <NoteBlock
              icon={HardHat}
              title={t("crew_packet.section_labor")}
              body={packet.labor_notes}
              empty={t("crew_packet.labor_empty")}
              testid="crew-packet-labor"
            />
            <NoteBlock
              icon={ClipboardList}
              title={t("crew_packet.section_internal")}
              body={packet.internal_crew_notes}
              empty={t("crew_packet.internal_empty")}
              testid="crew-packet-internal"
            />
          </section>
        </div>
      ) : null}
    </DashboardLayout>
  );
};

const NoteBlock = ({ icon: Icon, title, body, empty, testid }) => (
  <div data-testid={testid}>
    <div className="overline mb-1.5 flex items-center gap-1.5">
      <Icon size={13} /> {title}
    </div>
    {body ? (
      <p className="text-sm whitespace-pre-wrap">{body}</p>
    ) : (
      <p className="text-sm text-[var(--jc-text-soft)]">{empty}</p>
    )}
  </div>
);

export default CrewPacket;
